import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useScopeStream, type CacheRequestEvent } from '../hooks/useScopeStream.js';
import { WireDetailsDrawer } from './WireDetailsDrawer.js';
import { redactString } from '../lib/phi.js';

export interface NetworkTabProps {
  sessionId: string;
  /** Whether the import is still active. Drives the SSE-vs-REST fallback. */
  isLive: boolean;
  /** Reveal PHI toggle from parent. Drives URL display + drawer rendering. */
  reveal: boolean;
}

interface NetworkRow {
  id: string;       // raw_cache row id (or live-event id for in-flight rows)
  method: string;
  url: string;
  status: number;
  durationMs: number;
  bytes: number;
  endpoint: string;
  fetchedAt: string | null; // null for live-only rows that haven't been persisted yet
  /** Live rows have no rowId yet; clicking them opens the drawer once persisted. */
  rowIdForDrawer: string | null;
}

type SortKey = 'time' | 'endpoint' | 'method' | 'url' | 'status' | 'duration' | 'bytes';
type SortDir = 'asc' | 'desc';

/**
 * Browser-devtools-style HTTP transparency view. Renders a sortable, filterable
 * table of every HTTP request the cache layer made for this session. When the
 * import is still running, prefer the live SSE feed; otherwise hit the REST
 * endpoint to read from raw_cache.
 *
 * Click any row to open the wire-details drawer.
 */
export function NetworkTab({ sessionId, isLive, reveal }: NetworkTabProps) {
  const { cacheRequests } = useScopeStream(sessionId);

  // REST fallback for completed sessions (or to seed the table on initial load).
  const { data: restData, refetch } = useQuery({
    queryKey: ['cache-network', sessionId],
    queryFn: () => api.getCacheNetwork(sessionId, { limit: 500 }),
    refetchInterval: isLive ? false : 10_000, // refresh slowly when settled
  });

  // Merge: live events override REST entries with the same fetched timestamp,
  // since the REST data lags behind. We dedupe by URL + fetchedAt epoch.
  const rows = useMemo<NetworkRow[]>(() => {
    const liveRows: NetworkRow[] = (cacheRequests ?? []).map((r: CacheRequestEvent) => ({
      id: `live-${r.id}`,
      method: r.method,
      url: r.url,
      status: r.status,
      durationMs: r.durationMs,
      bytes: r.bytes,
      endpoint: '', // live event doesn't carry endpoint name in current shape — set empty
      fetchedAt: new Date(r.ts).toISOString(),
      rowIdForDrawer: null,
    }));
    const restRows: NetworkRow[] = (restData?.requests ?? []).map((r) => ({
      id: r.id,
      method: r.method,
      url: r.url,
      status: r.status,
      durationMs: r.durationMs,
      bytes: r.bytes,
      endpoint: r.endpoint,
      fetchedAt: r.fetchedAt,
      rowIdForDrawer: r.id,
    }));
    // Prefer rest rows (they have rowIdForDrawer); only include live rows that
    // don't have a matching rest row by url+timestamp-bucket. Bucket is 2s to
    // tolerate the lag between SSE event and DB write.
    const restKeys = new Set(
      restRows
        .filter((r) => r.fetchedAt)
        .map((r) => `${r.url}|${Math.floor(new Date(r.fetchedAt!).getTime() / 2000)}`),
    );
    const liveOnly = liveRows.filter((r) => {
      if (!r.fetchedAt) return true;
      const key = `${r.url}|${Math.floor(new Date(r.fetchedAt).getTime() / 2000)}`;
      return !restKeys.has(key);
    });
    return [...liveOnly, ...restRows];
  }, [cacheRequests, restData]);

  // Sort + filter
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [statusFilter, setStatusFilter] = useState<'all' | 'ok' | 'error'>('all');
  const [endpointFilter, setEndpointFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [drawerRowId, setDrawerRowId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter === 'ok') out = out.filter((r) => r.status >= 200 && r.status < 400);
    else if (statusFilter === 'error') out = out.filter((r) => r.status === 0 || r.status >= 400);
    if (endpointFilter) out = out.filter((r) => r.endpoint === endpointFilter);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((r) => r.url.toLowerCase().includes(q) || r.endpoint.toLowerCase().includes(q));
    }
    return out;
  }, [rows, statusFilter, endpointFilter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'time': cmp = String(a.fetchedAt ?? '').localeCompare(String(b.fetchedAt ?? '')); break;
        case 'endpoint': cmp = a.endpoint.localeCompare(b.endpoint); break;
        case 'method': cmp = a.method.localeCompare(b.method); break;
        case 'url': cmp = a.url.localeCompare(b.url); break;
        case 'status': cmp = a.status - b.status; break;
        case 'duration': cmp = a.durationMs - b.durationMs; break;
        case 'bytes': cmp = a.bytes - b.bytes; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const endpoints = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.endpoint) set.add(r.endpoint);
    return Array.from(set).sort();
  }, [rows]);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('desc'); }
  }

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Filter by URL or endpoint…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg px-3 py-1.5 text-sm font-mono flex-1 min-w-[200px]"
          style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(148,163,184,0.25)', color: 'var(--text-primary)' }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'ok' | 'error')}
          className="rounded-lg px-3 py-1.5 text-sm"
          style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(148,163,184,0.25)', color: 'var(--text-primary)' }}
        >
          <option value="all">All statuses</option>
          <option value="ok">2xx / 3xx</option>
          <option value="error">4xx / 5xx</option>
        </select>
        {endpoints.length > 0 && (
          <select
            value={endpointFilter}
            onChange={(e) => setEndpointFilter(e.target.value)}
            className="rounded-lg px-3 py-1.5 text-sm"
            style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(148,163,184,0.25)', color: 'var(--text-primary)' }}
          >
            <option value="">All endpoints</option>
            {endpoints.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-lg px-3 py-1.5 text-xs"
          style={{ background: 'rgba(148,163,184,0.18)', color: 'rgb(203,213,225)', border: '1px solid rgba(148,163,184,0.45)' }}
        >
          ↻ Refresh
        </button>
        <span className="text-xs text-text-dimmed font-mono">
          {sorted.length.toLocaleString()} of {rows.length.toLocaleString()} requests
        </span>
      </div>

      {/* Table */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(148,163,184,0.18)' }}
      >
        <div className="grid text-[10px] uppercase tracking-widest text-text-secondary font-mono px-3 py-2 border-b border-border"
             style={{ gridTemplateColumns: '110px 130px 60px 1fr 60px 70px 70px' }}>
          <ColHeader label="Time" active={sortKey === 'time'} dir={sortDir} onClick={() => toggleSort('time')} />
          <ColHeader label="Endpoint" active={sortKey === 'endpoint'} dir={sortDir} onClick={() => toggleSort('endpoint')} />
          <ColHeader label="Method" active={sortKey === 'method'} dir={sortDir} onClick={() => toggleSort('method')} />
          <ColHeader label="URL" active={sortKey === 'url'} dir={sortDir} onClick={() => toggleSort('url')} />
          <ColHeader label="Status" active={sortKey === 'status'} dir={sortDir} onClick={() => toggleSort('status')} />
          <ColHeader label="Duration" active={sortKey === 'duration'} dir={sortDir} onClick={() => toggleSort('duration')} />
          <ColHeader label="Bytes" active={sortKey === 'bytes'} dir={sortDir} onClick={() => toggleSort('bytes')} />
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {sorted.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-text-dimmed italic">
              No requests captured yet.
            </div>
          )}
          {sorted.map((r) => {
            const ok = r.status >= 200 && r.status < 400;
            const url = reveal ? r.url : redactString(r.url, 'url');
            const path = (() => { try { return new URL(url).pathname + new URL(url).search; } catch { return url; } })();
            const time = r.fetchedAt ? new Date(r.fetchedAt).toLocaleTimeString(undefined, { hour12: false }) : '—';
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => r.rowIdForDrawer && setDrawerRowId(r.rowIdForDrawer)}
                disabled={!r.rowIdForDrawer}
                className="grid w-full text-left px-3 py-1.5 text-xs font-mono border-b border-border/30 hover:bg-white/5 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
                style={{ gridTemplateColumns: '110px 130px 60px 1fr 60px 70px 70px' }}
                title={r.rowIdForDrawer ? 'Click for full wire details' : 'Live row — details available once persisted'}
              >
                <span className="text-text-dimmed truncate">{time}</span>
                <span className="text-text-secondary truncate" title={r.endpoint}>{r.endpoint || '—'}</span>
                <span className="text-text-dimmed">{r.method}</span>
                <span className="text-text-primary truncate" title={url}>{path}</span>
                <span style={{ color: ok ? 'rgb(94,234,212)' : 'rgb(253,164,175)' }}>{r.status || '—'}</span>
                <span className="text-text-dimmed">{r.durationMs ? `${r.durationMs}ms` : '—'}</span>
                <span className="text-text-dimmed">{formatBytes(r.bytes)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <WireDetailsDrawer
        sessionId={sessionId}
        rowId={drawerRowId}
        reveal={reveal}
        onClose={() => setDrawerRowId(null)}
      />
    </div>
  );
}

function ColHeader({ label, active, dir, onClick }: { label: string; active: boolean; dir: SortDir; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left hover:text-text-primary transition-colors"
      style={{ color: active ? 'var(--text-primary)' : undefined }}
    >
      {label}{active && (dir === 'asc' ? ' ▲' : ' ▼')}
    </button>
  );
}

function formatBytes(n: number): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
